import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Eye, Folder, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Swatch } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field, Textarea } from '@/components/ui/Field'
import { Card, LoadingPanel } from '@/components/ui/misc'
import { api, ApiError } from '@/lib/api'
import { formatRelative } from '@/lib/utils'

interface QueueProject {
  id: string
  name: string
  description: string | null
  submittedAt: string | null
  submittedByName: string | null
  publishedAt: string | null
  experimentCount: number
}

interface ProjectExperiment {
  id: string
  code: string
  title: string
  swatchHex: string | null
  researcherName: string | null
  dyeSourceName: string | null
  publishedCode: string | null
}

/**
 * Projects awaiting approval. The reviewer reads every experiment in the project
 * and approves them together, which publishes each one as a public copy.
 */
export function ProjectReviewQueue({ onPreview }: { onPreview: (recordId: string) => void }) {
  const [reviewing, setReviewing] = useState<QueueProject | null>(null)

  const { data } = useQuery({
    queryKey: ['review', 'projects'],
    queryFn: () => api.get<{ projects: QueueProject[] }>('/review/projects'),
  })

  const projects = data?.projects ?? []
  if (projects.length === 0) return null

  return (
    <section className="mb-6">
      <h3 className="pb-2 font-display text-lg font-semibold text-ink">Projects awaiting approval</h3>
      <div className="space-y-2">
        {projects.map((project) => (
          <Card key={project.id} className="flex items-center gap-3 p-4">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] border border-line bg-paper-muted">
              <Folder className="size-5 text-brand" />
            </span>
            <div className="min-w-0 flex-1">
              <h4 className="truncate font-display text-[15px] font-semibold text-ink">{project.name}</h4>
              <p className="mt-0.5 text-[12px] text-ink-muted">
                {project.experimentCount} experiment{project.experimentCount === 1 ? '' : 's'} ·
                submitted by {project.submittedByName ?? 'the owner'}{' '}
                {formatRelative(project.submittedAt)}
                {project.publishedAt ? ' · republication' : ''}
              </p>
            </div>
            <Button size="sm" onClick={() => setReviewing(project)}>
              <Eye className="size-3.5" />
              Review project
            </Button>
          </Card>
        ))}
      </div>

      <ProjectReviewDialog
        project={reviewing}
        onClose={() => setReviewing(null)}
        onPreview={onPreview}
      />
    </section>
  )
}

function ProjectReviewDialog({
  project,
  onClose,
  onPreview,
}: {
  project: QueueProject | null
  onClose: () => void
  onPreview: (recordId: string) => void
}) {
  const queryClient = useQueryClient()
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')

  const detail = useQuery({
    queryKey: ['review', 'projects', project?.id],
    queryFn: () =>
      api.get<{ experiments: ProjectExperiment[] }>(`/review/projects/${project!.id}`),
    enabled: project !== null,
  })

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'reject') =>
      api.post<{ status: string; published: number }>(`/review/projects/${project!.id}/decision`, {
        decision,
        reason: decision === 'reject' ? reason.trim() : undefined,
      }),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['review'] }),
        queryClient.invalidateQueries({ queryKey: ['workspace'] }),
        queryClient.invalidateQueries({ queryKey: ['browse'] }),
      ])
      toast.success(
        result.status === 'published'
          ? `Approved. ${result.published} experiment${result.published === 1 ? ' is' : 's are'} now in the Public Database.`
          : 'Returned to the project team with your feedback.',
      )
      setRejecting(false)
      setReason('')
      onClose()
    },
    onError: (failure) =>
      toast.error(failure instanceof ApiError ? failure.message : 'That review did not save.'),
  })

  const experiments = detail.data?.experiments ?? []

  return (
    <Dialog
      open={project !== null}
      onOpenChange={(open) => {
        if (!open) {
          setRejecting(false)
          onClose()
        }
      }}
      size="lg"
      title={project ? `Review ${project.name}` : ''}
      description={project?.description ?? undefined}
      footer={
        rejecting ? (
          <>
            <Button variant="secondary" onClick={() => setRejecting(false)}>
              Back
            </Button>
            <Button loading={decide.isPending} disabled={!reason.trim()} onClick={() => decide.mutate('reject')}>
              Send back to the team
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => setRejecting(true)}>
              <X className="size-3.5" />
              Request changes
            </Button>
            <Button
              loading={decide.isPending}
              disabled={experiments.length === 0}
              onClick={() => decide.mutate('approve')}
            >
              <Check className="size-3.5" />
              Approve and publish {experiments.length} experiment{experiments.length === 1 ? '' : 's'}
            </Button>
          </>
        )
      }
    >
      {rejecting ? (
        <Field
          label="What needs changing?"
          htmlFor="project-reason"
          required
          hint="The project owner sees this verbatim. Name the experiments that need work."
        >
          <Textarea
            id="project-reason"
            rows={4}
            autoFocus
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      ) : detail.isLoading ? (
        <LoadingPanel label="Loading experiments" />
      ) : (
        <ul className="divide-y divide-line rounded-[var(--radius-control)] border border-line">
          {experiments.map((experiment) => (
            <li key={experiment.id} className="flex items-center gap-3 px-3 py-2.5">
              <Swatch hex={experiment.swatchHex} size="md" className="rounded-[var(--radius-control)]" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
                  {experiment.code}
                  {experiment.publishedCode && (
                    <span className="normal-case text-positive"> · updates {experiment.publishedCode}</span>
                  )}
                </p>
                <p className="truncate text-sm font-semibold text-ink">
                  {experiment.title || 'Untitled experiment'}
                </p>
                <p className="truncate text-[12px] text-ink-muted">
                  {[experiment.researcherName, experiment.dyeSourceName].filter(Boolean).join(' · ')}
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={() => onPreview(experiment.id)}>
                <Eye className="size-3.5" />
                Read
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  )
}
